import { ProductFeatureId } from '@/actions/permissionActions'
import { ReduxState } from '@/utils/typeHelpers'
import { createSelector } from 'reselect'
import { logHeardError } from '@/utils/errorHelpers'
import { RouteConfig } from '@/components/Routes/config'

export const selectUserPermissions = (state: ReduxState) =>
  state.auth.security.permissions

export const selectUserHasPermission = createSelector(
  selectUserPermissions,
  (_: unknown, permission: ProductFeatureId) => permission,
  (permissions, permission) => permissions.includes(permission)
)

export const selectUserHasPermissions = createSelector(
  selectUserPermissions,
  (_: unknown, permissions: ProductFeatureId[] | undefined) => permissions,
  (permissions, permissionsToCheck) =>
    permissionsToCheck
      ? permissionsToCheck.every((permission) =>
          permissions.includes(permission)
        )
      : true
)

export const hasPermissionsForRoute = (
  permissions: ProductFeatureId[],
  allRoutes: RouteConfig,
  routePath: string | undefined
): boolean => {
  if (!routePath) {
    return true
  }
  const routeConfig = Object.entries(allRoutes).find(
    ([path]) => path === routePath || path === routePath.replace(/\/$/, '') // Remove trailing slash for comparison
  )
  if (routeConfig) {
    const parentRoutesWithPermissionChecks = Object.entries(allRoutes).filter(
      ([path, route]) =>
        route.requiredPermissions?.includeChildRoutes &&
        routePath.startsWith(`${path}/`)
    )
    const isChildRoute = parentRoutesWithPermissionChecks.some(
      ([path, _route]) => routePath.startsWith(path)
    )
    let hasAllParentPermissions = true
    if (isChildRoute) {
      hasAllParentPermissions = parentRoutesWithPermissionChecks.every(
        ([_, parentRoute]) => {
          const requiredRoutePermissions =
            parentRoute.requiredPermissions?.features
          return requiredRoutePermissions
            ? requiredRoutePermissions.every((permission) =>
                permissions.includes(permission)
              )
            : true
        }
      )
    }
    const requiredRoutePermissions =
      routeConfig?.[1]?.requiredPermissions?.features
    const hasRequiredPermissions = requiredRoutePermissions
      ? requiredRoutePermissions.every((permission) =>
          permissions.includes(permission)
        )
      : true
    return hasAllParentPermissions && hasRequiredPermissions
  }
  logHeardError('Route config not found', { routePath })
  return true
}

export const selectUserHasPermissionsForRoute = createSelector(
  selectUserPermissions,
  (_: unknown, allRoutes: RouteConfig) => allRoutes,
  (_: unknown, __: unknown, routePath: string | undefined) => routePath,
  (permissions, allRoutes, routePath) =>
    hasPermissionsForRoute(permissions, allRoutes, routePath)
)
